import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Users as UsersIcon } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import { social as socialApi, auth as authApi } from '../lib/api';

interface ActiveUser {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  _count?: {
    shares: number;
    comments: number;
  };
}

export default function WelcomeTour() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user has seen the welcome tour from user object
    if (user && !user.hasSeenWelcomeTour) {
      loadActiveUsers();
    }
  }, [user]);

  async function loadActiveUsers() {
    setLoading(true);
    try {
      const allUsers = await socialApi.getAllUsers(token);

      // Sort by activity (shares + comments) and take top 6
      const sortedByActivity = allUsers
        .filter(u => u.id !== user?.id) // Exclude current user
        .sort((a, b) => {
          const aActivity = (a._count?.shares || 0) + (a._count?.comments || 0);
          const bActivity = (b._count?.shares || 0) + (b._count?.comments || 0);
          return bActivity - aActivity;
        })
        .slice(0, 6);

      if (sortedByActivity.length > 0) {
        setActiveUsers(sortedByActivity);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Failed to load active users:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleClose() {
    setIsOpen(false);
    // Mark tour as seen in database
    try {
      await authApi.markWelcomeTourSeen(token);
      // Update local user state
      const updateUser = useAuthStore.getState().updateUser;
      if (user) {
        updateUser({ ...user, hasSeenWelcomeTour: true });
      }
    } catch (error) {
      console.error('Failed to mark welcome tour as seen:', error);
    }
  }

  function handleViewProfile(userId: string) {
    handleClose();
    navigate(`/user/${userId}`);
  }

  function handleViewAllUsers() {
    handleClose();
    navigate('/users');
  }

  if (!isOpen || loading || activeUsers.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-canvas rounded-lg shadow-xl max-w-2xl w-full pointer-events-auto overflow-hidden">
          {/* Header */}
          <div className="bg-primary-500 text-white p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <UsersIcon className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">Meet Your Community</h2>
                </div>
                <p className="text-primary-100">
                  Here are some of the most active members on this instance
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:text-primary-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Active Users Grid */}
          <div className="p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {activeUsers.map((activeUser) => (
                <div
                  key={activeUser.id}
                  onClick={() => handleViewProfile(activeUser.id)}
                  className="flex items-start gap-3 p-4 border border-edge rounded-lg hover:border-primary-500 hover:shadow-md transition-all cursor-pointer"
                >
                  {/* Avatar */}
                  {activeUser.avatar ? (
                    <img
                      src={activeUser.avatar}
                      alt={activeUser.username}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0 avatar-duotone"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-white text-lg font-semibold flex-shrink-0">
                      {(activeUser.displayName?.[0] || activeUser.username[0]).toUpperCase()}
                    </div>
                  )}

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-ink truncate">
                      {activeUser.displayName || activeUser.username}
                    </h3>
                    {activeUser.displayName && (
                      <p className="text-sm text-ink-muted truncate">@{activeUser.username}</p>
                    )}
                    {activeUser.bio && (
                      <p className="text-sm text-ink-muted line-clamp-2 mt-1">
                        {activeUser.bio}
                      </p>
                    )}
                    {activeUser._count && (
                      <div className="flex items-center gap-3 text-xs text-ink-muted mt-2">
                        {activeUser._count.shares > 0 && (
                          <span>{activeUser._count.shares} shares</span>
                        )}
                        {activeUser._count.comments > 0 && (
                          <span>{activeUser._count.comments} comments</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleViewAllUsers}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                View All Users
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-ink-secondary rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
